const CAL_API_BASE = 'https://api.cal.eu/v2';
const TEAM_SLUG = 'clinlexis';
const EVENT_TYPE_SLUG = 'product-demo';

const SLOTS_API_VERSION = '2024-09-04';
const BOOKINGS_API_VERSION = '2024-08-13';

export interface SlotsByDate {
  [date: string]: string[];
}

interface RawSlotsResponse {
  status: 'success' | 'error';
  data?: Record<string, { start: string }[]>;
  error?: { message?: string };
}

export async function getSlots(params: {
  start: Date;
  end: Date;
  timeZone: string;
  signal?: AbortSignal;
}): Promise<SlotsByDate> {
  const url = new URL(`${CAL_API_BASE}/slots`);
  url.searchParams.set('teamSlug', TEAM_SLUG);
  url.searchParams.set('eventTypeSlug', EVENT_TYPE_SLUG);
  url.searchParams.set('start', params.start.toISOString());
  url.searchParams.set('end', params.end.toISOString());
  url.searchParams.set('timeZone', params.timeZone);

  const res = await fetch(url.toString(), {
    headers: { 'cal-api-version': SLOTS_API_VERSION },
    signal: params.signal,
  });
  if (!res.ok) {
    throw new Error(`Failed to load availability (${res.status})`);
  }
  const json = (await res.json()) as RawSlotsResponse;
  if (json.status !== 'success' || !json.data) {
    throw new Error(json.error?.message ?? 'Failed to load availability');
  }
  const out: SlotsByDate = {};
  for (const [date, slots] of Object.entries(json.data)) {
    out[date] = slots.map(s => s.start);
  }
  return out;
}

export class SlotTakenError extends Error {
  constructor() {
    super('That slot is no longer available — please pick another.');
    this.name = 'SlotTakenError';
  }
}

interface RawBookingResponse {
  status: 'success' | 'error';
  data?: { uid: string };
  error?: { code?: string; message?: string };
}

export interface CreateBookingParams {
  start: string;
  name: string;
  email: string;
  timeZone: string;
  notes?: string;
  metadata?: Record<string, string>;
}

export async function createBooking(params: CreateBookingParams): Promise<{ uid: string }> {
  const body: Record<string, unknown> = {
    start: params.start,
    eventTypeSlug: EVENT_TYPE_SLUG,
    teamSlug: TEAM_SLUG,
    attendee: {
      name: params.name,
      email: params.email,
      timeZone: params.timeZone,
      language: 'en',
    },
  };
  if (params.metadata && Object.keys(params.metadata).length > 0) {
    body.metadata = params.metadata;
  }
  if (params.notes) {
    body.bookingFieldsResponses = { notes: params.notes };
  }

  const res = await fetch(`${CAL_API_BASE}/bookings`, {
    method: 'POST',
    headers: {
      'cal-api-version': BOOKINGS_API_VERSION,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  let json: RawBookingResponse | null = null;
  try {
    json = (await res.json()) as RawBookingResponse;
  } catch {
    // fall through to status-based error below
  }

  if (!res.ok || json?.status === 'error') {
    const message = (json?.error?.message ?? '').toLowerCase();
    if (
      res.status === 409 ||
      message.includes('not available') ||
      message.includes('already booked') ||
      message.includes('no_available_users')
    ) {
      throw new SlotTakenError();
    }
    throw new Error(json?.error?.message ?? `Booking failed (${res.status})`);
  }

  return { uid: json?.data?.uid ?? '' };
}
